import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { useRazorpay, RazorpayOrderOptions } from 'react-razorpay';
import axios from 'axios';

const Payment = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const { error: razorpayError, isLoading: razorpayLoading, Razorpay } = useRazorpay();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user || !user._id) {
      navigate('/login');
    } else if (!user.is_registered) {
      navigate('/register-details');
    } else if (user.is_paid) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handlePayment = async () => {
    setLoading(true);
    setError('');

    try {
      // Create Razorpay order
      const { data: orderData } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/payment/create-order`,
        {
          amount: user.referral_discount || user.discount_amount ? 12500 : 15000,
          referral_code: user.referral_code_used || ''
        },
        { withCredentials: true }
      );

      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'MyGaadiNumber',
        description: 'Lifetime Subscription',
        order_id: orderData.order_id,
        handler: async (response) => {
          try {
            // Verify payment
            const { data: verifyData } = await axios.post(
              `${process.env.REACT_APP_BACKEND_URL}/api/payment/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              },
              { withCredentials: true }
            );

            if (verifyData.success) {
              // Update user state
              const { data: updatedUser } = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
                { withCredentials: true }
              );
              updateUser(updatedUser);
              navigate('/dashboard');
            }
          } catch (err) {
            setError('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: user.name || '',
          email: user.email || '',
          contact: user.alt_contact || ''
        },
        theme: {
          color: '#FF3B30'
        }
      };

      const razorpayInstance = new Razorpay(options);
      razorpayInstance.open();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Payment initiation failed');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  const baseAmount = 150;
  const discount = user.discount_amount || (user.referral_code_used ? 25 : 0);
  const finalAmount = baseAmount - discount;

  return (
    <div className="min-h-screen bg-black py-24 px-6">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#121214] border border-white/10 rounded-2xl p-8"
        >
          <h2 className="text-3xl font-medium mb-2 tracking-tight">Complete Your Payment</h2>
          <p className="text-[#A1A1A6] mb-8">Secure payment powered by Razorpay</p>

          <div className="bg-[#0A0A0B] p-6 rounded-xl border border-white/10 mb-8">
            <h3 className="text-xl font-medium mb-4">Order Summary</h3>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2">
                <span className="text-[#A1A1A6]">Lifetime Subscription</span>
                <span className="text-lg font-medium">₹{baseAmount}</span>
              </div>
              
              {discount > 0 && (
                <div className="flex justify-between items-center py-2 text-[#32D74B]">
                  <span>Referral Discount</span>
                  <span>-₹{discount}</span>
                </div>
              )}
              
              <div className="border-t border-white/10 pt-4 mt-4">
                <div className="flex justify-between items-center">
                  <span className="text-xl font-medium">Total Amount</span>
                  <span className="text-3xl font-medium text-[#FF3B30]">₹{finalAmount}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#0A0A0B] p-6 rounded-xl border border-white/10 mb-8">
            <h4 className="font-medium mb-3">What You Get:</h4>
            <ul className="space-y-2 text-sm text-[#A1A1A6]">
              <li className="flex items-start gap-2">
                <span className="text-[#32D74B] mt-0.5">✓</span>
                <span>Lifetime access to all features</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#32D74B] mt-0.5">✓</span>
                <span>Emergency blood group search by vehicle number</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#32D74B] mt-0.5">✓</span>
                <span>Store up to 3 vehicles and 3 family members</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#32D74B] mt-0.5">✓</span>
                <span>Unlimited profile updates</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#32D74B] mt-0.5">✓</span>
                <span>Unique referral code to earn rewards</span>
              </li>
            </ul>
          </div>

          {error && (
            <div className="bg-[#FF453A]/10 border border-[#FF453A]/20 rounded-lg p-4 text-[#FF453A] mb-6" data-testid="payment-error-message">
              {error}
            </div>
          )}

          <button
            onClick={handlePayment}
            disabled={loading || razorpayLoading}
            data-testid="pay-now-button"
            className="w-full px-8 py-4 bg-[#FF3B30] hover:bg-[#E3242B] rounded-full text-lg font-medium transition-all disabled:opacity-50"
          >
            {loading || razorpayLoading ? 'Processing...' : `Pay ₹${finalAmount} Now`}
          </button>

          <p className="text-center text-[#86868B] text-sm mt-6">
            Secure payment processed by Razorpay. Your payment information is encrypted and secure.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Payment;
